import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Lock, CheckCircle, X, ShieldCheck } from "lucide-react";
import AdminLayout from "../components/admin/AdminLayout";
import { useAdminAuth } from "../services/admin/AdminAuthServices";

import { useNotification } from "../context/NotificationContext";

export default function ChangePassword() {
    const navigate = useNavigate();
    const { changePasswordAPI, loading: isSubmitting } = useAdminAuth();
    const { showNotification } = useNotification();

    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState("");

    // Password strength calculations
    const hasMinLength = newPassword.length >= 8;
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasDigit = /\d/.test(newPassword);
    const allRequirementsMet = hasMinLength && hasUpperCase && hasDigit;
    const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;

    // Calculate password strength
    const getPasswordStrength = () => {
        if (!newPassword) return { label: "", color: "", width: "0%" };
        let strength = 0;
        if (hasMinLength) strength++;
        if (hasUpperCase) strength++;
        if (hasDigit) strength++;
        if (newPassword.length >= 12) strength++;
        if (/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)) strength++;

        if (strength <= 2)
            return { label: "Weak", color: "bg-red-500", width: "33%" };
        if (strength <= 3)
            return { label: "Medium", color: "bg-yellow-500", width: "66%" };
        return { label: "Strong", color: "bg-green-500", width: "100%" };
    };

    const strength = getPasswordStrength();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!allRequirementsMet) {
            setError("Please meet all password requirements");
            return;
        }

        if (!passwordsMatch) {
            setError("New passwords do not match");
            return;
        }

        if (currentPassword === newPassword) {
            setError("New password cannot be the same as current password");
            return;
        }

        try {
            const res = await changePasswordAPI({
                current_password: currentPassword.trim(),
                new_password: newPassword.trim(),
                confirm_password: confirmPassword.trim()
            });

            if (res && res.status === "success") {
                navigate("/admin/dashboard");
            }
        } catch (err) {
            console.error("Change password error:", err);
            const msg = err.response?.data?.message || err.response?.data?.error || "Failed to update password. Please check your current password and try again.";
            setError(msg);
        }
    };

    return (
        <AdminLayout>
            <div className="max-w-2xl mx-auto py-8 px-4">
                <div className="mb-8">
                    <h1 className="text-2xl md:text-3xl font-bold text-[#111111] dark:text-white mb-2 font-poppins">
                        Change Password
                    </h1>
                    <p className="text-[#666666] dark:text-[#A0A0A0] font-inter">
                        Update your account security by creating a new password
                    </p>
                </div>

                <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl shadow-sm border border-[#E5E7EB] dark:border-[#2A2A2A] p-6 md:p-8">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {error && (
                            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 animate-fade-in">
                                <p className="text-red-600 dark:text-red-400 text-sm font-inter">
                                    {error}
                                </p>
                            </div>
                        )}

                        {/* Current Password */}
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-[#111111] dark:text-white font-inter">
                                Current Password
                            </label>
                            <div className="relative">
                                <ShieldCheck
                                    className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#666666] dark:text-[#A0A0A0]"
                                    size={18}
                                />
                                <input
                                    type={showCurrentPassword ? "text" : "password"}
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    className="w-full pl-12 pr-12 py-3 bg-[#F9FAFB] dark:bg-[#0A0A0A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl text-[#111111] dark:text-white placeholder-[#999999] dark:placeholder-[#666666] font-inter focus:outline-none focus:ring-2 focus:ring-[#62397f] transition-all"
                                    placeholder="Enter current password"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[#666666] dark:text-[#A0A0A0] hover:text-[#62397f] transition-colors"
                                >
                                    {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>

                        <hr className="border-[#E5E7EB] dark:border-[#2A2A2A]" />

                        {/* New Password */}
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-[#111111] dark:text-white font-inter">
                                New Password
                            </label>
                            <div className="relative">
                                <Lock
                                    className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#666666] dark:text-[#A0A0A0]"
                                    size={18}
                                />
                                <input
                                    type={showNewPassword ? "text" : "password"}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className="w-full pl-12 pr-12 py-3 bg-[#F9FAFB] dark:bg-[#0A0A0A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl text-[#111111] dark:text-white placeholder-[#999999] dark:placeholder-[#666666] font-inter focus:outline-none focus:ring-2 focus:ring-[#62397f] transition-all"
                                    placeholder="Enter new password"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[#666666] dark:text-[#A0A0A0] hover:text-[#62397f] transition-colors"
                                >
                                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>

                        {newPassword && (
                            <div className="space-y-3 animate-fade-in bg-[#F9FAFB] dark:bg-[#0A0A0A] p-4 rounded-xl border border-[#E5E7EB] dark:border-[#2A2A2A]">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-medium text-[#666666] dark:text-[#A0A0A0] font-inter uppercase tracking-wider">
                                        Password Strength
                                    </span>
                                    <span
                                        className={`text-xs font-bold ${strength.color.replace("bg-", "text-")} font-inter uppercase tracking-wider`}
                                    >
                                        {strength.label}
                                    </span>
                                </div>
                                <div className="w-full h-1.5 bg-[#E5E7EB] dark:bg-[#2A2A2A] rounded-full overflow-hidden">
                                    <div
                                        className={`h-full ${strength.color} transition-all duration-300`}
                                        style={{ width: strength.width }}
                                    ></div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-4">
                                    <div
                                        className={`flex items-center gap-1.5 text-[11px] ${hasMinLength ? "text-green-600 dark:text-green-400" : "text-[#666666] dark:text-[#A0A0A0]"} font-inter`}
                                    >
                                        {hasMinLength ? <CheckCircle size={14} /> : <X size={14} />}
                                        <span>8+ characters</span>
                                    </div>
                                    <div
                                        className={`flex items-center gap-1.5 text-[11px] ${hasUpperCase ? "text-green-600 dark:text-green-400" : "text-[#666666] dark:text-[#A0A0A0]"} font-inter`}
                                    >
                                        {hasUpperCase ? <CheckCircle size={14} /> : <X size={14} />}
                                        <span>Uppercase</span>
                                    </div>
                                    <div
                                        className={`flex items-center gap-1.5 text-[11px] ${hasDigit ? "text-green-600 dark:text-green-400" : "text-[#666666] dark:text-[#A0A0A0]"} font-inter`}
                                    >
                                        {hasDigit ? <CheckCircle size={14} /> : <X size={14} />}
                                        <span>Number</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Confirm Password */}
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-[#111111] dark:text-white font-inter">
                                Confirm New Password
                            </label>
                            <div className="relative">
                                <Lock
                                    className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#666666] dark:text-[#A0A0A0]"
                                    size={18}
                                />
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="w-full pl-12 pr-12 py-3 bg-[#F9FAFB] dark:bg-[#0A0A0A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl text-[#111111] dark:text-white placeholder-[#999999] dark:placeholder-[#666666] font-inter focus:outline-none focus:ring-2 focus:ring-[#62397f] transition-all"
                                    placeholder="Confirm new password"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[#666666] dark:text-[#A0A0A0] hover:text-[#62397f] transition-colors"
                                >
                                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                            {confirmPassword && (
                                <p
                                    className={`text-xs font-medium font-inter mt-1.5 ${passwordsMatch ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}
                                >
                                    {passwordsMatch
                                        ? "✓ Passwords match"
                                        : "✗ Passwords do not match"}
                                </p>
                            )}
                        </div>

                        <div className="pt-4 flex flex-col sm:flex-row gap-4">
                            <button
                                type="submit"
                                disabled={isSubmitting || !allRequirementsMet || !passwordsMatch || !currentPassword}
                                className="flex-1 py-3.5 px-6 bg-gradient-to-r from-[#62397f] to-[#7C3AED] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 font-poppins disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                            >
                                {isSubmitting ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                                fill="none"
                                            />
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            />
                                        </svg>
                                        Updating Password...
                                    </span>
                                ) : (
                                    "Update Password"
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => navigate("/admin/dashboard")}
                                className="flex-1 py-3.5 px-6 bg-white dark:bg-[#1A1A1A] text-[#666666] dark:text-[#A0A0A0] font-semibold rounded-xl border border-[#E5E7EB] dark:border-[#2A2A2A] hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-all duration-200 font-poppins"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <style jsx="true" global="true">{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.4s ease-out; }
      `}</style>
        </AdminLayout>
    );
}
